import { decodeJwtClaims, type JwtClaims } from "./claims";

// Claim names the backend's GetEmail() reads (ClaimTypes.Email, then "email").
const EMAIL_CLAIMS = [
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
  "email",
];

function getClaimString(claims: JwtClaims, key: string): string | undefined {
  const value = claims[key];
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  return trimmed || undefined;
}

/** The backend's email claim precedence, shared by session and flag identities. */
export function getSessionEmail(claims: JwtClaims): string | undefined {
  for (const key of EMAIL_CLAIMS) {
    const email = getClaimString(claims, key);
    if (email) return email;
  }
}

/**
 * Identifies the signed-in user across token refreshes. Anything scoped to one
 * user (the hub connection, server permission snapshots) is rebuilt when it changes.
 */
export function getSessionIdentityKey(claims: JwtClaims): string {
  return JSON.stringify([
    getClaimString(claims, "sub"),
    getSessionEmail(claims),
  ]);
}

/**
 * Mirrors the backend's JWT-only callers: bounded (`bnd`) tokens, and tokens
 * with a subject but no email, which CallerPermissionEvaluator treats as M2M.
 * Those callers have no server-side session permissions.
 */
export function usesTokenPermissionsOnly(
  accessToken: string,
  sessionClaims: JwtClaims,
): boolean {
  let claims = sessionClaims;

  try {
    claims = decodeJwtClaims(accessToken);
  } catch {
    // Opaque tokens fall back to the merged session claims.
  }

  if ("bnd" in claims) return true;

  return !getSessionEmail(claims) && Boolean(getClaimString(claims, "sub"));
}
